"use client";

import React, { useState } from "react";
import Image from "next/image";
import { getStrapiMedia } from "@/utils/api-helpers";
import BottomCurves from "../curves/bottomCurves";
import FloatingArrow from "../uiDecor/floatingArrow";
import PlusIcon from "../uiDecor/plusIcon";
import { getBackgroundColor } from "@/utils/colorHelpers";

type AccordionItem = {
    id: number;
    heading: string;
    description: string;
};

type ChapterAccordionProps = {
    data: {
        id: number;
        heading: string;
        label: string;
        description?: string;
        media?: {
            url: string;
        };
        accordianTitle?: string;
        contents: AccordionItem[];
        waveBgColor: {
            waveBgColor: string;
        };
        backgroundColor: {
            backgroundColor: string;
        };
    };
};

export default function ChapterAccordion({ data }: ChapterAccordionProps) {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
     const background = getBackgroundColor(
            data.backgroundColor?.backgroundColor);

    const toggle = (index: number) => {
        setActiveIndex(index === activeIndex ? null : index);
    };
    const isChapter4 = data.label === "Chapter 4";
    const sectionIdMap: Record<string, string> = {
        'Chapter 1': 'cdp-why',
        'Chapter 4': 'cdp-why-antsomi',
    }
    const sectionId = sectionIdMap[data.label]

    return (
        <section
            id={sectionId}
            className={`px-8 sm:px-6 lg:px-[10vw]  ${
                isChapter4 ? "py-4 md:py-4 lg:py-4" : "py-16 md:py-24 lg:py-32"
            }`}
            style={{ background }}
        >
            <FloatingArrow
                direction="right"
                className="top-0 right-[10.625vw]"
                wrapText={data.label?.toLowerCase().replace(" ", "-")}
            />

            <div className="max-w-[93rem] mx-auto">
                {/* Chapter Label */}
                {data.label && (
                    <div className="flex items-center gap-2 mb-4">
                        <span className="w-3 h-3 bg-purple rounded-full"></span>
                        <span className="text-purple font-semibold text-sm md:text-base">
                            {data.label}
                        </span>
                    </div>
                )}

                {/* Heading */}
                {data.heading && (
                    <h2 className="text-gray-900 font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight mb-6 max-w-4xl uppercase tracking-tight">
                        {data.heading}
                    </h2>
                )}

                {/* Description */}
                {data.description && (
                    <div
                        className="text-gray-600 text-base md:text-lg lg:text-xl max-w-5xl leading-relaxed"
                        dangerouslySetInnerHTML={{
                            __html: data.description,
                        }}
                    />
                )}

                {/* Image + Accordion Section */}
                <div className="flex flex-col lg:flex-row lg:gap-16 items-start">
                    {/* Image */}
                    {data.media?.url && (
                        <div className="w-full lg:w-1/2">
                            <Image
                                src={getStrapiMedia(data.media.url)}
                                width={600}
                                height={500}
                                alt={data.heading || "Chapter illustration"}
                                className="w-full h-auto object-contain"
                            />
                        </div>
                    )}

                    {/* Accordion */}
                    <div className="w-full lg:w-1/2 mt-12">
                        {/* Accordion Intro */}
                        {data.accordianTitle && (
                            <div
                                className="w-full"
                                dangerouslySetInnerHTML={{
                                    __html: data.accordianTitle,
                                }}
                            />
                        )}

                        {/* Accordion Items */}
                        <div className="flex flex-col divide-y divide-gray-300">
                            {data.contents?.map((item, index) => {
                                const isOpen = index === activeIndex;
                                return (
                                    <div
                                        key={item.id}
                                        onClick={() => toggle(index)}
                                        className="py-4 md:py-5 cursor-pointer transition-all"
                                    >
                                        <div className="flex justify-between items-center gap-4">
                                            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 select-none">
                                                {item.heading}
                                            </h3>
                                            {/* Plus/Minus Icon */}
                                            <div className="w-6 h-6 md:w-7 md:h-7 flex-shrink-0">
                                                <PlusIcon
                                                    color={
                                                        isOpen
                                                            ? "#851BED"
                                                            : "#851BED"
                                                    }
                                                    rotate={isOpen}
                                                />
                                            </div>
                                        </div>

                                        {/* Accordion Content */}
                                        <div
                                            className={`overflow-hidden transition-all duration-500 ease-in-out ${
                                                isOpen
                                                    ? "max-h-96 mt-3 opacity-100"
                                                    : "max-h-0 opacity-0"
                                            }`}
                                        >
                                            <p className="text-base md:text-lg text-gray-600 font-normal select-none leading-relaxed whitespace-pre-line">
                                                {item.description}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {data.waveBgColor && (
                <BottomCurves
                    color={data.waveBgColor.waveBgColor}
                    direction="right"
                />
            )}
        </section>
    );
}
